import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '@htx/environment';
import { RepresentativeChangeRequest, RepresentativeChangeComment } from './representative-change.model';
import {
  PageQueryParams,
  PaginatedList,
  SpringPageApiDto
} from '../organizations/organization.model';
import { HttpUtilsService } from '../http-utils.service';

@Injectable({
  providedIn: 'root'
})
export class RepresentativeChangeService {

  private httpClient = inject(HttpClient);
  private httpUtils = inject(HttpUtilsService);
  private readonly baseUrl = `${environment.backendUrl}/representative-change-requests`;

  createRequest(newMemberId: string, reasoning: string, document: File): Observable<RepresentativeChangeRequest> {
    const formData = new FormData();
    formData.append('newMemberId', newMemberId);
    if (reasoning) {
      formData.append('reasoning', reasoning);
    }
    formData.append('document', document);
    return this.httpClient.post<RepresentativeChangeRequest>(this.baseUrl, formData);
  }

  findAll(): Observable<RepresentativeChangeRequest[]> {
    return this.httpClient.get<RepresentativeChangeRequest[]>(this.baseUrl);
  }

  getDetailsById(id: string): Observable<RepresentativeChangeRequest> {
    return this.httpClient.get<RepresentativeChangeRequest>(`${this.baseUrl}/${id}`);
  }

  approve(id: string, reasoning: string): Observable<void> {
    return this.httpClient.put<void>(`${this.baseUrl}/${id}/approve`, { reasoning });
  }

  reject(id: string, reasoning: string): Observable<void> {
    return this.httpClient.put<void>(`${this.baseUrl}/${id}/reject`, { reasoning });
  }

  getDocument(id: string): Observable<Blob> {
    return this.httpClient.get(`${this.baseUrl}/${id}/document`, {
      responseType: 'blob'
    });
  }

  getComments(
    id: string,
    queryParams: PageQueryParams = { page: 0, size: 5, sort: 'date,desc' }
  ): Observable<PaginatedList<RepresentativeChangeComment>> {
    const params = this.httpUtils.toHttpParams(queryParams);
    return this.httpClient
      .get<SpringPageApiDto<RepresentativeChangeComment>>(`${this.baseUrl}/${id}/comments`, { params })
      .pipe(
        map((response) => ({
          content: response.content,
          pagination: {
            pageNumber: response.pageable.pageNumber,
            pageSize: response.pageable.pageSize,
            totalElements: response.totalElements,
            totalPages: response.totalPages,
            last: response.last,
            first: response.first,
            numberOfElements: response.numberOfElements,
            size: response.size,
            number: response.number,
            empty: response.empty
          },
          sort: response.sort
        }))
      );
  }

  addComment(id: string, body: { text: string }): Observable<RepresentativeChangeComment> {
    return this.httpClient.post<RepresentativeChangeComment>(`${this.baseUrl}/${id}/comments`, body);
  }
}
